/* eslint-disable no-param-reassign */
import { filterSetToDefault } from 'components/SimInventoryFilters/shared';
import { isObject } from 'lodash';
import isArray from 'lodash/isArray';
import { Interval } from 'luxon';
import type { DateFilter, TextFilter } from 'types/common';
import inputDateToDateTime from 'utils/dates/inputDateToDateTime';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist, type StateStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { ValidFilterKeys, type StoredFilters } from './SimInventoryStore';
import { SimInventoryState } from './types';

const STORE_NAME = 'simInventory' as const;
const STORE_VERSION = 10 as const;

const allDateFilters: Set<keyof StoredFilters> = new Set([
  'lastActive',
  'activationDate',
  'targetActivationDate',
]);
const allFancyTextFilters: Set<keyof StoredFilters> = new Set(['iccid', 'imei']);
const arrayLikeFilters: Set<keyof StoredFilters> = new Set(['simStatuses', 'tags', 'plans']);
const underscoreEncodedFilters: Set<keyof StoredFilters> = new Set(['simStatuses']);
const numericFilters: Set<keyof StoredFilters> = new Set(['plans', 'tags', 'organizationId']);
const booleanFilters: Set<keyof StoredFilters> = new Set(['noTags']);

// Paraphrased from https://zustand.docs.pmnd.rs/guides/connect-to-state-with-url-hash#persist-and-connect-state-with-url-parameters-(example:-url-query-parameters)

const getUrlSearch = () => window.location.search.slice(1);
const setUrlParams = (newParams: string) => window.history.replaceState({}, '', `?${newParams}`);
const clearUrlParams = () => window.history.replaceState({}, '', window.location.pathname);

const isDateFilter = (key: keyof StoredFilters, _value: any): _value is DateFilter =>
  allDateFilters.has(key);

const isFancyTextFilter = (key: keyof StoredFilters, _value: any): _value is TextFilter =>
  allFancyTextFilters.has(key);

const isValidFilterKey = (key: string): key is keyof StoredFilters =>
  ValidFilterKeys.has(key as any);

const persistentStorage: StateStorage = {
  getItem: (key): string => {
    // Migrate the URLs to a format the storage can understand
    const urlSearch = getUrlSearch();
    const searchParams = new URLSearchParams(urlSearch);
    const newFilters: any = {};

    [...searchParams.entries()].forEach(([filterKey, filterValue]) => {
      const storedFilterKey = filterKey as keyof StoredFilters;

      if (filterKey.includes('-')) {
        const [primaryKey, subKey] = filterKey.split('-');
        if (!isValidFilterKey(primaryKey)) {
          return;
        }

        if (subKey === 'timeframe') {
          newFilters[primaryKey] = {
            ...newFilters[primaryKey],
            timeframe: decodeURI(filterValue),
          };
        } else if (subKey === 'start') {
          newFilters[primaryKey] = {
            ...newFilters[primaryKey],
            start: decodeURI(filterValue),
          };
        } else if (subKey === 'end') {
          newFilters[primaryKey] = {
            ...newFilters[primaryKey],
            end: decodeURI(filterValue),
          };
        } else if (subKey === 'condition') {
          newFilters[primaryKey] = {
            ...newFilters[primaryKey],
            condition: decodeURI(filterValue),
          };
        } else if (subKey === 'value') {
          newFilters[primaryKey] = {
            ...newFilters[primaryKey],
            value: decodeURI(filterValue),
          };
        }
      } else {
        if (!isValidFilterKey(storedFilterKey)) {
          return;
        }

        if (arrayLikeFilters.has(storedFilterKey)) {
          if (underscoreEncodedFilters.has(storedFilterKey)) {
            newFilters[filterKey] = filterValue
              .split(',')
              .map((value: string) => decodeURI(value.replaceAll('_', ' ')));
          } else if (numericFilters.has(storedFilterKey)) {
            newFilters[filterKey] = filterValue.split(',').map((value) => parseInt(value, 10));
          } else {
            newFilters[filterKey] = filterValue.split(',').map((value) => decodeURI(value));
          }
        } else if (underscoreEncodedFilters.has(storedFilterKey)) {
          newFilters[filterKey] = decodeURI(filterValue.replaceAll('_', ' '));
        } else if (numericFilters.has(storedFilterKey)) {
          newFilters[filterKey] = parseInt(filterValue, 10);
        } else if (booleanFilters.has(storedFilterKey)) {
          newFilters[filterKey] = filterValue === 'true';
        } else {
          newFilters[filterKey] = decodeURI(filterValue);
        }
      }
    });

    // See if there exists existing state
    const localStoreRaw = localStorage.getItem(key);
    let localStore = localStoreRaw ? JSON.parse(localStoreRaw) : '{}';

    if (Object.keys(newFilters).length > 0) {
      const localStoreObject = JSON.parse(localStore);

      if (!localStoreObject.state) {
        localStoreObject.state = {};
      }

      localStoreObject.state.filters = newFilters;

      if (!localStoreObject.state.sorts) {
        localStoreObject.state.sorts = [];
      }

      if (!localStoreObject.version) {
        localStoreObject.version = STORE_VERSION;
      }

      localStore = JSON.stringify(localStoreObject);
    }

    return localStore;
  },
  setItem: (key, newValue): void => {
    const { state: newState } = JSON.parse(newValue);

    // Clean out empty
    const newFilters = Object.fromEntries(
      Object.entries(newState.filters as StoredFilters).filter(([filterKey, filterValue]) => {
        if (!filterValue) {
          return false;
        }

        if (isArray(filterValue)) {
          return filterValue.length > 0;
        }

        if (!isValidFilterKey(filterKey)) {
          return false;
        }

        if (isDateFilter(filterKey as keyof StoredFilters, filterValue)) {
          return !filterSetToDefault(filterKey, filterValue);
        }

        if (isObject(filterValue)) {
          return Object.keys(filterValue).length > 0;
        }

        return !!filterValue;
      })
    );

    const searchParams: Array<[string, string]> = [];

    Object.entries(newFilters).forEach(([filterKey, filterValue]) => {
      if (isArray(filterValue)) {
        if (underscoreEncodedFilters.has(filterKey as keyof StoredFilters)) {
          searchParams.push([
            filterKey,
            filterValue
              .map((value: string) => encodeURIComponent(value.replaceAll(' ', '_')))
              .join(','),
          ]);
        } else {
          searchParams.push([
            filterKey,
            filterValue.map((value: string) => encodeURIComponent(value)).join(','),
          ]);
        }
      } else if (isDateFilter(filterKey as keyof StoredFilters, filterValue)) {
        searchParams.push([`${filterKey}-timeframe`, encodeURIComponent(filterValue.timeframe!)]);
        if (filterValue.timeframe === 'custom') {
          searchParams.push([`${filterKey}-start`, encodeURIComponent(filterValue.start!)]);
          searchParams.push([`${filterKey}-end`, encodeURIComponent(filterValue.end!)]);
        }
      } else if (isFancyTextFilter(filterKey as keyof StoredFilters, filterValue)) {
        searchParams.push([`${filterKey}-condition`, encodeURIComponent(filterValue.condition)]);
        searchParams.push([`${filterKey}-value`, encodeURIComponent(filterValue.value)]);
      } else if (isObject(filterValue)) {
        searchParams.push([filterKey, encodeURIComponent(JSON.stringify(filterValue))]);
      } else {
        searchParams.push([filterKey, encodeURIComponent(filterValue)]);
      }
    });

    const urlParams = searchParams
      .map(([filterKey, filterValue]) => `${filterKey}=${filterValue}`)
      .join('&');

    // Sync filters to the URL
    if (urlParams && urlParams.trim().length > 0) {
      setUrlParams(urlParams);
    } else {
      clearUrlParams();
    }

    // Always save the entire state to localStorage
    localStorage.setItem(key, JSON.stringify(newValue));
  },
  removeItem: (key): void => {
    clearUrlParams();
    localStorage.removeItem(key);
  },
};

export type SimInventoryStore = ReturnType<typeof createSimInventoryStore>;

const createSimInventoryStore = (storeKey: string) =>
  create<SimInventoryState>()(
    persist(
      immer(
        devtools(
          (set) => ({
            filters: {} as any,
            sorts: [] as any,

            /* Overall */
            reset: () =>
              set(
                (state) => {
                  state.filters = {};
                  state.sorts = [];
                },
                true,
                { type: 'simInventory/reset' }
              ),

            /* Filters */
            resetFilters: () =>
              set(
                (state) => {
                  state.filters = {};
                },
                false,
                { type: 'simInventory/resetFilters' }
              ),
            setOrganizationIdFilter: (organizationId) =>
              set(
                (state) => {
                  if (!organizationId) {
                    delete state.filters.organizationId;
                  } else {
                    state.filters.organizationId = organizationId;
                  }
                },
                false,
                { type: 'simInventory/setOrganizationIdFilter', organizationId }
              ),
            setDeviceNameFilter: (deviceName) =>
              set(
                (state) => {
                  if (!deviceName) {
                    delete state.filters.deviceName;
                  } else {
                    state.filters.deviceName = deviceName;
                  }
                },
                false,
                { type: 'simInventory/setDeviceNameFilter', deviceName }
              ),
            setIccidFilter: (iccid) =>
              set(
                (state) => {
                  state.filters.iccid = iccid;
                },
                false,
                { type: 'simInventory/setIccidFilter', iccid }
              ),
            setImeiFilter: (imei) =>
              set(
                (state) => {
                  state.filters.imei = imei;
                },
                false,
                { type: 'simInventory/setImeiFilter', imei }
              ),
            setLastActiveFilter: (lastActive) =>
              set(
                (state) => {
                  state.filters.lastActive = lastActive;
                },
                false,
                { type: 'simInventory/setLastActiveFilter', lastActive }
              ),
            setActivationDateFilter: (activationDate) =>
              set(
                (state) => {
                  state.filters.activationDate = activationDate;
                },
                false,
                { type: 'simInventory/setActivationDateFilter', activationDate }
              ),
            setTargetActivationDateFilter: (targetActivationDate) =>
              set(
                (state) => {
                  state.filters.targetActivationDate = targetActivationDate;
                },
                false,
                { type: 'simInventory/setTargetActivationDateFilter', targetActivationDate }
              ),
            setSimStatusFilter: (simStatuses) =>
              set(
                (state) => {
                  state.filters.simStatuses = simStatuses;
                },
                false,
                { type: 'simInventory/setSimStatusFilter', simStatuses }
              ),
            setTagFilter: (tags) =>
              set(
                (state) => {
                  state.filters.tags = tags;
                },
                false,
                { type: 'simInventory/setTagFilter', tags }
              ),
            // TODO: Consolidate with above
            setNoTagFilter: (noTags) =>
              set(
                (state) => {
                  state.filters.noTags = noTags;
                },
                false,
                { type: 'simInventory/setNoTagFilter', noTags }
              ),
            setPlanFilter: (plans) =>
              set(
                (state) => {
                  state.filters.plans = plans;
                },
                false,
                { type: 'simInventory/setPlanFilter', plans }
              ),

            /* Sorts */
            resetSorts: () =>
              set(
                (state) => {
                  state.sorts = [];
                },
                false,
                { type: 'simInventory/resetSorts' }
              ),
            setSort: (sort) =>
              set(
                (state) => {
                  // For now, there can only be one, everything else is the defaults
                  if (sort) {
                    if (isArray(sort)) {
                      state.sorts = sort;
                    } else {
                      state.sorts = [sort];
                    }
                  } else {
                    state.sorts = [];
                  }
                },
                false,
                { type: 'simInventory/setSort' }
              ),
          }),
          {
            name: `${STORE_NAME}-${storeKey}`,
            enabled: (process.env.VITE_ENV_NAME ?? 'development') === 'development',
          }
        )
      ),
      {
        name: `${STORE_NAME}-${storeKey}`,
        version: STORE_VERSION,
        onRehydrateStorage: () => (state?: SimInventoryState, error?: any) => {
          if (error) {
            return;
          }

          allDateFilters.forEach((key) => {
            const dateFilter = state?.filters[key] as DateFilter | undefined;

            const startDate = inputDateToDateTime(dateFilter?.start);
            const endDate = inputDateToDateTime(dateFilter?.end);

            if (
              dateFilter?.timeframe === 'custom' &&
              (!startDate || !endDate || !Interval.fromDateTimes(startDate, endDate).isValid)
            ) {
              delete state?.filters[key];
            } else if (dateFilter?.timeframe !== 'custom' && (startDate || endDate)) {
              // Always re-compute these
              delete (state?.filters[key] as any)?.start;
              delete (state?.filters[key] as any)?.end;
            }
          });
        },
        storage: createJSONStorage<SimInventoryState>(() => persistentStorage),
      }
    )
  );

export default createSimInventoryStore;
